import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Phone, Heart, Clock } from 'lucide-react';
import { siteConfig, pricing } from '../../data';
import ContactSection from '../../components/ContactSection';

export default function Tryghedssamtale() {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Tryghedssamtale | Personlig rådgivning | Online Tryghed</title>
        <meta name="description" content="Få en rolig og tryg samtale om din sikkerhed på nettet. Vi hjælper dig med at forstå truslerne og beskytte dig selv." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-slate-50 overflow-hidden border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#103B66] mb-6 leading-tight">
              Tryghedssamtale: Din vej til ro i maven på nettet
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Er du usikker på, om din computer er sikker? Eller har du hørt om svindel og er blevet bange for at bruge netbank? En tryghedssamtale er en rolig gennemgang af din digitale hverdag.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="px-8 py-4 bg-[#103B66] text-white font-bold hover:bg-slate-800 transition-all shadow-md">
                Book en samtale nu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Banner (Full Width) */}
      <section className="bg-gradient-to-r from-[#103B66] to-slate-900 text-white py-12 relative overflow-hidden border-b border-slate-800">
        <div className="absolute right-0 top-0 translate-x-1/3 -translate-y-1/3 w-96 h-96 bg-blue-600/10 rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider mb-4">
                <Clock className="w-3.5 h-3.5" />
                Kom godt i gang
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold mb-3">
                {pricing.specialOffer.title} til kun <span className="text-blue-400">{pricing.specialOffer.price}</span>
              </h3>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                {pricing.specialOffer.description}
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-6 py-3.5 bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all text-sm shadow-md"
              >
                <Phone className="w-4 h-4 mr-2" />
                Ring og book nu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pt-8 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#103B66] mb-6">Hvad er en tryghedssamtale?</h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Mange ældre oplever en voksende utryghed ved den digitale verden. Overskrifter om hacking, svindel og identitetstyveri kan gøre det svært at nyde de fordele, som internettet ellers giver.
                </p>
                <p>
                  En tryghedssamtale er ikke et teknisk kursus, men en personlig dialog, hvor vi tager udgangspunkt i dine bekymringer. Vi gennemgår dine vaner, tjekker dine indstillinger og giver dig konkrete værktøjer til at spotte svindel, før det sker.
                </p>
                <p>
                  Vi kommer gerne hjem til dig, så vi kan sidde i vante omgivelser ved din egen computer eller tablet.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 p-8 rounded-none border border-slate-100">
              <h3 className="text-2xl font-bold text-[#103B66] mb-6">Det får du ud af samtalen:</h3>
              <ul className="space-y-4">
                {[
                  "Gennemgang af dine adgangskoder og sikkerhed",
                  "Lær at genkende falske mails og SMS'er",
                  "Tryghed ved brug af MitID og netbank",
                  "Svar på alle de spørgsmål, du ikke tør stille andre",
                  "En konkret handlingsplan for din digitale sikkerhed"
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <ShieldCheck className="w-6 h-6 text-black mr-3 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-100 text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Hvorfor vælge en tryghedssamtale?</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">Vi lægger vægt på det menneskelige aspekt af IT-sikkerhed.</p>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-none shadow-sm">
              <Heart className="w-12 h-12 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4">Tålmodighed</h3>
              <p className="text-slate-600">Vi forklarer tingene i et sprog, der er til at forstå. Ingen spørgsmål er for små eller "dumme".</p>
            </div>
            <div className="bg-white p-8 rounded-none shadow-sm">
              <Clock className="w-12 h-12 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4">God tid</h3>
              <p className="text-slate-600">Vi har ikke travlt. Vi bliver, indtil du føler dig tryg og har fået svar på dine spørgsmål.</p>
            </div>
            <div className="bg-white p-8 rounded-none shadow-sm">
              <Phone className="w-12 h-12 text-black mb-6" />
              <h3 className="text-xl font-bold mb-4">Opfølgning</h3>
              <p className="text-slate-600">Efter samtalen kan du altid ringe, hvis du bliver i tvivl om noget, vi har gennemgået.</p>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
